import { useState, type ReactNode } from "react";
import { clockTime, phaseLabel } from "../../lib/labels.js";
import { outputWindow, stepDetail, stepTone } from "../../lib/stepPresentation.js";
import { logGrowth, useStickToBottom } from "../../lib/useStickToBottom.js";
import type { Step, StepIo } from "../../lib/viewModel.js";

/**
 * "Inside the shell" — the full account of the build, in the hatch skin's hand.
 *
 * This is the classic transcript's information, not a narrowing of it: every
 * step the build produced, each with its clock time, its repeat count and how
 * long it has been going, the files a burst wrote, and the command it ran with
 * everything that command printed. Phase crossings are section headings, the
 * end of the feed says so out loud, and the whole thing scrolls.
 *
 * It replaced a seven-line card that showed the build's present tense and threw
 * the rest away. That card was defensible while the egg stood in the middle of
 * the screen and there was nowhere to put more; with the scenery moved into its
 * own column there is a full pane here, and a build's history is worth more than
 * the empty space it was traded for.
 *
 * What differs from the classic transcript is presentation and one addition: the
 * card names its own totals. "12 files · 34 steps" is what the eye reaches for
 * when the question is how much has happened, and it is true of the build rather
 * than of what fits on screen.
 *
 * The follow-to-bottom behaviour is `lib/useStickToBottom`'s, shared with the
 * classic transcript. Nothing about being stuck to the bottom of a log is skin.
 */
export function ShellLog({
  steps,
  isTerminal,
  fileCount,
}: {
  steps: Step[];
  isTerminal: boolean;
  fileCount: number;
}) {
  const { scrollerRef, following, scrolled, jumpToLatest } = useStickToBottom(
    logGrowth(steps, isTerminal),
  );

  const lastIndex = steps.length - 1;

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border-2 border-shell-rule bg-card shadow-[0_6px_0_rgb(62_50_38_/_0.12)]">
      <header className="flex shrink-0 items-center justify-between gap-4 border-b-2 border-shell-rule px-5 py-3">
        <h2 className="text-[11px] font-extrabold tracking-[0.18em] uppercase text-bark-faint">
          Inside the shell
        </h2>
        {/* Totals, not what is on screen — otherwise a long build reports
            "4 steps" forever. Two elements rather than one string so each
            number is a thing on its own, which is also how they are read. */}
        <div className="flex shrink-0 items-baseline gap-1.5 font-plex text-[11.5px] text-bark-fainter">
          {fileCount > 0 && (
            <>
              <span>
                {fileCount} {fileCount === 1 ? "file" : "files"}
              </span>
              <span aria-hidden>&middot;</span>
            </>
          )}
          <span>
            {steps.length} {steps.length === 1 ? "step" : "steps"}
          </span>
        </div>
      </header>

      {/* The fades and the jump control are positioned against this box rather
          than the card, so neither can ride up over the header. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollerRef}
          // The generous bottom padding is the point: the last row gets clear
          // space under it instead of stopping against the frame, which is what
          // makes a finished feed look truncated.
          className="min-h-0 flex-1 overflow-y-auto px-4 pt-3 pb-12"
          role="log"
          aria-live="polite"
          aria-label="Build activity"
        >
          <ul className="space-y-0.5">
            {steps.map((step, i) => (
              <li key={step.id} className="animate-[tickIn_0.3s_ease]">
                <Row step={step} active={i === lastIndex && !isTerminal} />
              </li>
            ))}
          </ul>
          {steps.length > 0 && <Terminus isTerminal={isTerminal} />}
        </div>

        {/* Both fades sit over the scroller and are inert, so they dim content
            at the edges without intercepting a scroll or a click on a row. */}
        {scrolled && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-linear-to-b from-card to-transparent"
          />
        )}
        {!following && (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-card via-card/85 to-transparent"
            />
            <button
              type="button"
              onClick={jumpToLatest}
              className="absolute bottom-4 left-1/2 flex -translate-x-1/2 cursor-pointer items-center gap-1.5 rounded-full border-2 border-shell-rule bg-card px-3.5 py-1 text-[12px] font-extrabold text-bark-soft shadow-[0_3px_0_rgb(62_50_38_/_0.12)] transition-colors hover:border-ember hover:text-ember"
            >
              Jump to latest
              <span aria-hidden className="leading-none">
                ↓
              </span>
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * The bottom of the feed, said out loud.
 *
 * Without it the last row is the last thing on screen and there is no way to
 * tell a finished build from a clipped one. Live it doubles as the "still
 * running" cue, which is why the wording differs rather than the marker just
 * disappearing when the build ends.
 */
function Terminus({ isTerminal }: { isTerminal: boolean }) {
  return (
    <p className="mt-3 flex items-center gap-3 px-3 text-[10.5px] font-extrabold tracking-[0.16em] uppercase text-bark-faint">
      {isTerminal ? (
        <>
          <span aria-hidden className="h-px flex-1 bg-shell-rule" />
          End of build
          <span aria-hidden className="h-px flex-1 bg-shell-rule" />
        </>
      ) : (
        <>
          <span
            aria-hidden
            className="size-2 rounded-full bg-ember animate-[dotPulse_1s_ease-in-out_infinite]"
          />
          Working…
        </>
      )}
    </p>
  );
}

function Row({ step, active }: { step: Step; active: boolean }) {
  // A phase crossing is a section heading, not an event — the same reading the
  // classic transcript gives it. Without one, "Deploying" arrives in the feed as
  // just another sentence and the log stops saying where in the build it is.
  if (step.kind === "phase") {
    return (
      <p className="flex items-center gap-3 py-3 text-[10.5px] font-extrabold tracking-[0.16em] uppercase text-bark-faint">
        <span aria-hidden className="h-px flex-1 bg-shell-rule" />
        {phaseLabel(step.label)}
        <span aria-hidden className="h-px flex-1 bg-shell-rule" />
      </p>
    );
  }

  // An artifact step goes ember only when EVERY write in it failed — a partial
  // failure keeps the neutral dot and says "N failed" in its label, since
  // painting a mostly-successful burst as a failure would overstate it.
  // Which of these a step is, is not this skin's decision — see
  // lib/stepPresentation. What the colours are, is.
  const tone = stepTone(step);
  const failed = tone === "failed";
  const succeeded = tone === "succeeded";

  return (
    <div
      className={`flex gap-3 rounded-[8px] px-3 py-2 transition-colors ${
        active ? "bg-ember-soft" : ""
      }`}
    >
      {/*
        Ember means both "here, and now" and "this failed" — the hatch palette
        has one warm accent and no second red to spend. The two never collide:
        `active` is the last row of a build still running, and a failed step is
        terminal, so a row cannot be both. The pulse and the ember label settle
        any remaining doubt in opposite directions.
      */}
      <span className="flex w-3 shrink-0 justify-center pt-[7px]" aria-hidden>
        <span
          className={`size-2 rounded-full ${
            failed
              ? "bg-ember"
              : succeeded
                ? "bg-moss"
                : active
                  ? "bg-ember animate-[dotPulse_1s_ease-in-out_infinite]"
                  : "bg-pending"
          }`}
        />
      </span>

      <div className="min-w-0 flex-1">
        <p
          className={`text-[13.5px] leading-snug font-semibold ${
            failed
              ? "text-ember"
              : succeeded
                ? "text-moss font-extrabold"
                : "text-bark"
          }`}
        >
          {step.label}
        </p>

        <Detail step={step} active={active} />
        {step.io && <Io io={step.io} />}
      </div>

      <span className="shrink-0 pt-1 font-plex text-[11px] text-bark-fainter">
        {clockTime(step.at)}
      </span>
    </div>
  );
}

/**
 * The secondary line: how many times, how long, and which files.
 *
 * This is where repetition goes. A count here is one row saying "this happened
 * four times"; without it the same sentence occupies four rows and the reader
 * cannot tell progress from a loop.
 */
function Detail({ step, active }: { step: Step; active: boolean }) {
  const detail = stepDetail(step, active);
  if (!detail) return null;
  const { parts, paths, hiddenPaths } = detail;

  return (
    <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 font-plex text-[11.5px] text-bark-fainter">
      {parts.length > 0 && <span>{parts.join(" · ")}</span>}
      {paths.length > 0 && (
        <span className="min-w-0 truncate">
          {paths.join(", ")}
          {hiddenPaths > 0 && ` +${hiddenPaths}`}
        </span>
      )}
    </p>
  );
}

/**
 * A step's command and what it printed, as one block beneath the row.
 *
 * Both halves are labelled in a gutter rather than by punctuation, so a command
 * is distinguishable from its output at a glance even when both are shell text.
 */
function Io({ io }: { io: StepIo }) {
  const [expanded, setExpanded] = useState(false);
  const { shown, hidden } = outputWindow(io, expanded);

  return (
    <div className="mt-1.5 overflow-hidden rounded-[6px] border border-shell-rule bg-paper/50">
      {io.input !== undefined && (
        <IoRow label="in">
          <span className="text-bark-soft">{io.input}</span>
        </IoRow>
      )}

      {io.output.length > 0 && (
        <IoRow label="out" divided={io.input !== undefined}>
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mb-1 block cursor-pointer text-[11px] text-bark-fainter transition-colors hover:text-ember"
            >
              {expanded
                ? "Show less"
                : `+${hidden} earlier ${hidden === 1 ? "line" : "lines"}`}
            </button>
          )}
          {/*
            Every line reads the same, whichever stream it came out of.
            `stderr` used to be painted ember, and on a healthy build that is
            simply wrong: npm writes its progress there, Vite writes its build
            report there, and the deploy prints "Expires in 0h 58m" there. Ember
            means "here, and now" or "this failed", and neither is true of a
            tool that happened to pick the second file descriptor.

            What a step's outcome actually was is already said where it is
            known — the step's own dot and label — and that is the only place
            that can say it honestly.
          */}
          {shown.map((line) => (
            <div key={line.seq} className="text-bark-faint">
              {line.line}
            </div>
          ))}
        </IoRow>
      )}
    </div>
  );
}

function IoRow({
  label,
  divided,
  children,
}: {
  label: string;
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex gap-2.5 px-2.5 py-1.5 ${divided ? "border-t border-shell-rule" : ""}`}
    >
      <span className="w-5 shrink-0 pt-px text-[10px] font-extrabold tracking-[0.12em] uppercase text-bark-fainter">
        {label}
      </span>
      {/* `min-w-0` so an unbroken 300-character line wraps instead of widening
          the pane and forcing the whole card to scroll sideways. */}
      <div className="min-w-0 flex-1 font-plex text-[11px] leading-relaxed whitespace-pre-wrap break-all">
        {children}
      </div>
    </div>
  );
}
